import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { formatDate } from '../utils/dateFormatter';
import { Copy, Check, ThumbsUp, ThumbsDown, User, MessageSquare } from 'lucide-react';

/**
 * Single message display component with improved styling for nested content structures
 */
const MessageItem = React.memo(({ message }) => {
  const [copied, setCopied] = useState(false);
  const [reaction, setReaction] = useState(null);
  
  const isUser = message.sender === 'user';
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleReaction = (type) => {
    setReaction(reaction === type ? null : type);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-5 group`}>
      <div 
        className={`max-w-[90%] md:max-w-[80%] rounded-lg shadow-md ${
          isUser
            ? 'bg-emerald-500/50 border border-emerald-950'
            : 'bg-slate-700 border border-slate-600'
        }`}
      >
        {/* Message header with sender info */}
        <div className={`px-3 py-2 flex items-center gap-2 border-b ${
          isUser
            ? 'border-emerald-700/50 bg-emerald-900/30'
            : 'border-slate-600/50 bg-slate-800/50'
        }`}>
          <div className={`p-1.5 rounded-full ${
            isUser
              ? 'bg-emerald-700/50'
              : 'bg-slate-600/50'
          }`}>
            {isUser ? (
              <User className="w-3.5 h-3.5 text-white" />
            ) : (
              <MessageSquare className="w-3.5 h-3.5 text-emerald-300" />
            )}
          </div>
          <div className="flex-1">
            <span className={`text-xs font-medium ${
              isUser ? 'text-emerald-200' : 'text-emerald-300'
            }`}>
              {isUser ? 'You' : 'PickStar AI Assistant'}
            </span>
          </div>
          <time className="text-xs text-slate-300">
            {formatDate(message.timestamp)}
          </time>
        </div>
        
        {/* Message content with improved styling for nested structures */}
        <div className="p-4">
          <div className="prose prose-sm max-w-none 
            prose-p:text-white prose-p:leading-relaxed prose-p:my-2 
            prose-headings:text-emerald-200 
            prose-a:text-emerald-300 
            prose-code:bg-slate-800/90 prose-code:text-emerald-300 
            prose-pre:bg-slate-800/90 prose-pre:p-3
            prose-strong:text-emerald-200
            prose-ul:my-2 prose-li:text-white prose-li:my-1
            [&_ul]:text-white [&_li]:text-white 
            [&_li_strong]:text-emerald-300 [&_li_strong+span]:text-white
            [&_li]:pl-1">
            <ReactMarkdown components={{
              // Custom component for list items to ensure proper styling
              li: ({node, ...props}) => {
                return (
                  <li className="my-2 text-white" {...props} />
                )
              },
              // Custom component for strong text to ensure proper styling in lists
              strong: ({node, ...props}) => {
                return (
                  <strong className="text-emerald-300 font-semibold" {...props} />
                )
              }
            }}>
              {message.text || ''}
            </ReactMarkdown>
          </div>
        </div>
        
        {/* Actions footer with functionality */}
        <div className={`px-3 py-2 border-t flex items-center justify-between ${
          isUser
            ? 'border-emerald-700/50 bg-emerald-900/30'
            : 'border-slate-600/50 bg-slate-800/50'
        }`}>
          {!isUser ? (
            <div className="flex items-center gap-1">
              <button 
                onClick={() => handleReaction('thumbsUp')}
                className={`p-1.5 rounded-md transition-colors ${
                  reaction === 'thumbsUp'
                    ? 'bg-emerald-600/30 text-emerald-300'
                    : 'hover:bg-slate-600/50 text-slate-400 hover:text-emerald-300'
                }`}
                title="Helpful"
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={() => handleReaction('thumbsDown')}
                className={`p-1.5 rounded-md transition-colors ${
                  reaction === 'thumbsDown'
                    ? 'bg-red-600/30 text-red-300'
                    : 'hover:bg-slate-600/50 text-slate-400 hover:text-red-300'
                }`}
                title="Not helpful"
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="w-8"></div> // Empty spacer
          )}
          
          <button 
            onClick={handleCopy}
            className={`p-1.5 rounded-md transition-colors ${
              copied
                ? 'bg-emerald-600/30 text-emerald-300'
                : 'hover:bg-slate-600/50 text-slate-400 hover:text-emerald-300'
            }`}
            title={copied ? "Copied!" : "Copy message"}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
});

export default MessageItem;